import { Injectable } from '@angular/core';
import {
	IPublicClientApplication,
	InteractionType,
	LogLevel,
	PublicClientApplication,
	BrowserCacheLocation,
	AccountInfo
} from '@azure/msal-browser';
import { MsalGuardConfiguration, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { BehaviorSubject } from 'rxjs';
import { environment } from '@env/environment';
import { AppConfig } from '@app/shared/utils/config/app-config';
import { protectedResourceMap, updateProtectedResourceMap } from './protected-resource-map';

// Re-exported for backward compatibility (nothing else in the app should import these two from
// here anymore — import directly from './protected-resource-map' instead, see its file header for
// why this now lives in its own dependency-free module).
export { protectedResourceMap, updateProtectedResourceMap };

/**
 * Azure AD (MSAL) does not type the `roles` claim by default — it's tenant-specific,
 * only present once App Roles are declared + assigned in the App Registration.
 * // TODO v2: replace this ad-hoc typing once the real App Registration declares roles.
 */
export interface AppTokenClaims {
	roles?: string[];
	groups?: string[];
	onPremisesSamAccountName?: string;
	upn?: string;
}

/**
 * MSAL instance factory — used as `MSAL_INSTANCE` provider in `AppModule`.
 */
export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: environment.msalConfig.auth,
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage,
			storeAuthStateInCookie: false
		},
		system: {
			loggerOptions: {
				loggerCallback: () => {
					/* no-op by default — set LogLevel.Verbose + piiLoggingEnabled: true here temporarily for debugging (revert before merge, see TODO-NEXT.md) */
				},
				logLevel: LogLevel.Warning,
				piiLoggingEnabled: false
			}
		}
	});
}

/**
 * Interceptor configuration factory — used as `MSAL_INTERCEPTOR_CONFIG` provider.
 * See `protected-resource-map.ts`'s header comment for why this only does a best-effort
 * population (real population happens later, from `AppConfig.setConfigFile()`).
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	updateProtectedResourceMap(AppConfig.instance?.backendUrl);

	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap
	};
}

/**
 * Guard configuration factory — used as `MSAL_GUARD_CONFIG` provider.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		loginFailedRoute: '/not-found'
	};
}

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private readonly currentAccountSubject = new BehaviorSubject<AccountInfo | null>(null);
	public readonly currentAccount$ = this.currentAccountSubject.asObservable();

	constructor(private msalService: MsalService) {}

	public isLoggedIn(): boolean {
		return !!this.currentAccountSubject.value;
	}

	public getActiveAccount(): AccountInfo | null {
		return this.currentAccountSubject.value;
	}

	/**
	 * Must be called once `MsalBroadcastService.inProgress$` reaches `InteractionStatus.None`
	 * (see `AppComponent`) — never called from `APP_INITIALIZER` (see lesson #6 of the
	 * cognito-to-msal-migration skill: any protected HTTP call before the router has
	 * activated a route can leave a stale `interaction_in_progress` lock).
	 */
	public initCurrentUser(): void {
		let activeAccount = this.msalService.instance.getActiveAccount();
		if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
			const account = this.msalService.instance.getAllAccounts()[0];
			this.msalService.instance.setActiveAccount(account);
			activeAccount = account;
		}
		this.currentAccountSubject.next(activeAccount);
	}

	public logout(): void {
		this.msalService.logoutRedirect();
	}
}



